// Cards on file, on a customer's Money tab (BL-009): the cards kept for their renewals, saved on the
// card processor's own page (verify mode: nothing is charged, and the number never reaches us),
// and removed with the renewals on one moved to another card first. The server words every card and
// every refusal. The Helcim fake and the sandbox serve their stand-in page here, in the sheet's
// frame; real Helcim's opens over the page from its own script (HelcimPay.js), which the office
// pages' CSP allows by its one origin, and its answer is read only from that origin.

import { useEffect, useRef, useState } from 'react';
import { RowMore } from '../blueprint/RowMore.tsx';
import { client } from '../api.ts';
import { dayText } from '../books/model.ts';
import { errorText } from '../books/ui.tsx';
import { newId } from '../shared/ids.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import {
  cardPageAnswer,
  cardRights,
  FAKE_PROCESSOR,
  HELCIM_SCRIPT,
  removeOutcome,
  renewsText,
  replacements,
  type Card,
} from './cards-model.ts';
import './cards.css';

type Page = { pageId: string; checkoutToken: string; processor: string };

type HelcimWindow = Window & {
  appendHelcimPayIframe?: (checkoutToken: string) => void;
  removeHelcimPayIframe?: () => void;
};

/**
 * Takes HelcimPay's page off ours. Its frame is never left open behind a closed sheet, a page gone
 * or a page opened again: it could still take a card, saved in Helcim's vault with nobody here to
 * keep it (actions' review of 5df8d53a).
 */
const dropHelcimPage = () => (window as HelcimWindow).removeHelcimPayIframe?.();

/** Loads HelcimPay.js once, for the office's first card page. */
let helcim: Promise<HelcimWindow> | null = null;
function helcimPay(): Promise<HelcimWindow> {
  helcim ??= new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = HELCIM_SCRIPT;
    s.onload = () => resolve(window as HelcimWindow);
    s.onerror = () => {
      helcim = null;
      reject(new Error('The card processor’s page didn’t load. Check the connection and try again.'));
    };
    document.head.append(s);
  });
  return helcim;
}

export function CardsOnFile({
  customerId,
  customerName,
  inZone = false,
}: {
  customerId: string;
  customerName: string;
  /** Blueprint: a part of the record's work, not a section of its own */
  inZone?: boolean;
}) {
  const Part = inZone ? 'div' : 'section';
  const [cards, setCards] = useState<Card[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [again, setAgain] = useState(0);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<Card | null>(null);
  const can = cardRights(useMirror());
  useEffect(() => {
    client
      .call<{ cards: Card[] }>('cards.list', { customerId })
      .then((r) => setCards(r.cards))
      .catch((e: unknown) => setError(errorText(e, 'Could not read the cards on file.')));
  }, [customerId, again]);
  const done = (words: string) => {
    setSaid(words);
    setAgain((n) => n + 1);
  };
  return (
    <Part className="cards-on-file" aria-label="Cards on file">
      <div className="row between">
        <h3>Cards on file</h3>
        {can.save && (
          <button type="button" className="btn soft" onClick={() => setSaving(true)}>
            Save a card
          </button>
        )}
      </div>
      {/* while mirroring: no card saved, and a renewal's card stays (BL-088) */}
      {!can.save && <p className="muted small">Saving a card, and a renewal’s card: {MIRROR_LINE}</p>}
      {said && (
        <p className="notice ok" role="status">
          {said}
        </p>
      )}
      {error && <p className="error">{error}</p>}
      {cards && !cards.length && (
        <p className="muted">No card on file. A membership that renews by itself needs one.</p>
      )}
      {cards && cards.length > 0 && (
        <ul className="card-list">
          {cards.map((c) => {
            const renews = renewsText(c, dayText);
            return (
              <li key={c.id} className="card-row">
                <div>
                  <b>{c.label}</b>
                  {c.expired && <span className="chip say bad">Expired</span>}
                  {renews && <span className="muted small renews">{renews}</span>}
                </div>
                {/* Blueprint: under the card's More, so Money owed keeps its two actions */}
                {!can.remove(c) ? null : inZone ? (
                  <RowMore about={c.label}>
                    <button type="button" className="btn quiet" onClick={() => setRemoving(c)}>
                      Remove…
                    </button>
                  </RowMore>
                ) : (
                  <button type="button" className="btn quiet" onClick={() => setRemoving(c)}>
                    Remove…
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {saving && can.save && (
        <SaveCardSheet
          customerId={customerId}
          customerName={customerName}
          onSaved={(c) => done(`${c.label} saved to ${customerName}’s file.`)}
          onClose={() => setSaving(false)}
        />
      )}
      {removing && cards && (
        <RemoveCardSheet
          card={removing}
          cards={cards}
          onRemoved={(moved) =>
            done(
              moved
                ? `${removing.label} removed; ${moved === 1 ? 'its renewal goes' : `its ${moved} renewals go`} to the card you chose.`
                : `${removing.label} removed.`,
            )
          }
          onSaveAnother={() => {
            setRemoving(null);
            setSaving(true);
          }}
          onClose={() => setRemoving(null)}
        />
      )}
    </Part>
  );
}

function useSheet(onClose: () => void) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  return { ref, close: () => ref.current?.close(), onClose };
}

/** The processor's own page in a frame; its answer (a window message) keeps the card. */
function SaveCardSheet({
  customerId,
  customerName,
  onSaved,
  onClose,
}: {
  customerId: string;
  customerName: string;
  onSaved: (c: Card) => void;
  onClose: () => void;
}) {
  const s = useSheet(onClose);
  const [page, setPage] = useState<Page | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [keeping, setKeeping] = useState(false);
  // one keep per page: a second SUCCESS from the same page (a double post) is not sent again
  const kept = useRef(false);
  const [opened, setOpened] = useState(0);
  useEffect(() => {
    let live = true;
    kept.current = false;
    setPage(null);
    setError(null);
    client
      .call<Page>('cards.startSave', { pageId: newId(), customerId })
      .then(async (p) => {
        if (!live) return;
        setPage(p);
        // real Helcim: its own page opens over ours, never over one still open
        if (p.processor !== FAKE_PROCESSOR) {
          const w = await helcimPay();
          if (!live) return;
          dropHelcimPage();
          w.appendHelcimPayIframe?.(p.checkoutToken);
        }
      })
      .catch((e: unknown) => live && setError(errorText(e, 'Could not open the card page.')));
    // the sheet closed (Escape, Close, kept), the tab left, or the page opened again: its frame goes
    return () => {
      live = false;
      dropHelcimPage();
    };
  }, [customerId, opened]);
  useEffect(() => {
    if (!page) return;
    const listen = async (ev: MessageEvent) => {
      const a = cardPageAnswer(ev, page, window.location.origin);
      if (!a) return;
      if ('error' in a) return setError(a.error);
      if (kept.current) return;
      kept.current = true;
      setKeeping(true);
      setError(null);
      try {
        const c = await client.call<Card>('cards.finishSave', { pageId: page.pageId, answer: a });
        dropHelcimPage();
        onSaved(c);
        s.close();
      } catch (e) {
        kept.current = false; // its answer can be sent again
        setError(errorText(e, 'The card wasn’t kept. Try again.'));
      } finally {
        setKeeping(false);
      }
    };
    const on = (ev: MessageEvent) => void listen(ev);
    window.addEventListener('message', on);
    return () => window.removeEventListener('message', on);
  }, [page]);
  const expired = !!error && /expired/i.test(error);
  return (
    <dialog
      ref={s.ref}
      className="sheet ask card-sheet"
      aria-labelledby="card-sheet-title"
      onClose={() => {
        dropHelcimPage();
        onClose();
      }}
    >
      <h3 id="card-sheet-title" className="sheet-title">
        Save a card for {customerName}
      </h3>
      <p className="muted small">
        On the card processor’s own page: nothing is charged, and the card number never reaches us. Read it
        out with the customer, or have them type it.
      </p>
      {!page && !error && <p className="muted">Opening the card page…</p>}
      {page && page.processor !== FAKE_PROCESSOR && (
        <p className="muted">The card processor’s page is open: type the card there.</p>
      )}
      {page && page.processor === FAKE_PROCESSOR && (
        <iframe
          className="card-page"
          title="The card processor’s page"
          src={`/_helcim/checkout/${encodeURIComponent(page.checkoutToken)}`}
        />
      )}
      {keeping && (
        <p className="muted" role="status">
          Keeping the card…
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="row end">
        {expired && (
          <button type="button" className="btn soft" onClick={() => setOpened((n) => n + 1)}>
            Open it again
          </button>
        )}
        <button type="button" className="btn soft" onClick={s.close} disabled={keeping}>
          Close
        </button>
      </div>
    </dialog>
  );
}

function RemoveCardSheet({
  card,
  cards,
  onRemoved,
  onSaveAnother,
  onClose,
}: {
  card: Card;
  cards: Card[];
  onRemoved: (movedRenewals: number) => void;
  onSaveAnother: () => void;
  onClose: () => void;
}) {
  const s = useSheet(onClose);
  const others = replacements(cards, card);
  const [to, setTo] = useState(others[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needs = card.renews.length > 0;
  const replacement = needs ? others.find((c) => c.id === to) : undefined;
  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ cardId: string; movedRenewals: number }>('cards.remove', {
        cardId: card.id,
        ...(replacement ? { replacementCardId: replacement.id } : {}),
      });
      onRemoved(r.movedRenewals);
      s.close();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <dialog
      ref={s.ref}
      className="sheet ask card-sheet"
      aria-labelledby="remove-card-title"
      onClose={onClose}
    >
      <h3 id="remove-card-title" className="sheet-title">
        Remove {card.label.split(',')[0]}?
      </h3>
      <p className="card-outcome">{removeOutcome(card, replacement)}</p>
      {needs && others.length > 0 && (
        <label>
          Renew to
          <select className="field" value={to} onChange={(e) => setTo(e.target.value)}>
            {others.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {needs && !others.length && (
        <p className="muted small">
          There’s no other card on their file yet. Save one first, then remove this one.
        </p>
      )}
      <div className="row end">
        <button type="button" className="btn soft" onClick={s.close} disabled={busy}>
          Cancel
        </button>
        {needs && !others.length ? (
          <button type="button" className="btn primary" onClick={onSaveAnother}>
            Save another card
          </button>
        ) : (
          <SaveButton
            className="btn danger"
            busy={busy}
            busyLabel="Removing…"
            error={error}
            onClick={() => void remove()}
          >
            Remove the card
          </SaveButton>
        )}
      </div>
    </dialog>
  );
}
